import {
  DRINKING_WATER_SUPPLY_LABELS,
  PRESENCE_ASSESSMENT_LABELS,
  PUBLIC_NETWORK_TYPE_LABELS,
  SURVEY_METHODS,
  WASTEWATER_PRODUCTION_LABELS,
  type ConnectionBox,
  type PresenceAssessment,
  type TabletBlock,
  type SurveyV2,
  type VocabularyEntry,
} from '@ghp/domain'
import { Card, Field, Input, SegmentedControl } from '@ghp/ui'
import { useState, type ReactNode } from 'react'

import { IndoorFixtures, type IndoorFixturesProps } from './IndoorFixtures'
import { parseMeasure } from './StormwaterCard'
import s from './SurveyForm.module.css'

/**
 * Le relevé de terrain (M16), **version 2** : méthodes employées, terrain — quand la fiche du
 * client le demande (#355) —, points d'eau et équipements du raccordement — l'ordre de la fiche
 * papier (#258). L'éditeur y glisse ses cartes (#411) : bâtiments contrôlés et anomalies relevées
 * avant les points d'eau, eaux pluviales après. Les caractéristiques de
 * l'habitation sont la carte de l'onglet Infos (`DwellingCard`, #350).
 *
 * Présentationnel pur : le relevé arrive en prop, chaque changement ressort en entier. C'est
 * le conteneur qui décide d'écrire dans Dexie, pas ce composant.
 *
 * ⚠️ **Rien n'est obligatoire ici**, et c'est le cœur de la fiche (M21) : un relevé incomplet
 * s'enregistre, se transmet, et se signale à la validation. Une donnée de terrain ne se
 * reconstitue pas — l'agent est devant la maison, seul.
 */
export interface SurveyFormProps {
  survey: SurveyV2
  /**
   * Les blocs que la fiche de ce client ne demande pas (#355), déduits du rapport (`TABLET_BLOCKS`).
   * Masqués, jamais vidés — ce qui y était saisi reste dans le relevé (M21).
   */
  hiddenBlocks?: readonly TabletBlock[]
  /** Le référentiel en cache (M17) : les lignes et colonnes de la grille des points d'eau. */
  vocabulary?: readonly VocabularyEntry[]
  /** Fiche transmise : tout se lit, rien ne se saisit (D4). */
  disabled?: boolean
  onChange?: (survey: SurveyV2) => void
  /** Les anomalies relevées : la grille des points d'eau rappelle celles qu'elle désigne. */
  anomalies?: IndoorFixturesProps['anomalies']
  /** « Relever l'anomalie » sur un point d'eau compté « Non » — l'éditeur ouvre le constructeur. */
  onFlagFixture?: (fixture: string, location: string) => void
  /** Les cartes posées entre « Desserte de réseau EU » et « Points d'eau » (#411). */
  beforeFixtures?: ReactNode
  /** Les cartes posées entre « Points d'eau » et « Équipements de raccordement » (#411). */
  afterFixtures?: ReactNode
}

/**
 * Les quatre positions du tri-état de M16, plus l'absence de réponse.
 *
 * ⚠️ « Non renseigné » n'est pas « Éléments manquants » : l'agent qui n'a pas pu regarder ne
 * constate rien. Les confondre inventerait un constat que personne n'a fait — et le troisième
 * cran est bien un **constat** : l'agent a vu qu'il n'y avait rien (#119, confirmé le 09/09).
 */
const PRESENCE_OPTIONS = [
  { value: 'observed', label: PRESENCE_ASSESSMENT_LABELS.observed },
  { value: 'suspected', label: PRESENCE_ASSESSMENT_LABELS.suspected },
  { value: 'absent', label: PRESENCE_ASSESSMENT_LABELS.absent },
  { value: 'unset', label: 'Non renseigné' },
] as const

type PresenceChoice = (typeof PRESENCE_OPTIONS)[number]['value']

/** Oui / Non de la fiche papier, plus la case que personne n'a cochée. */
const PRESENT_OPTIONS = [
  { value: 'yes', label: 'Oui' },
  { value: 'no', label: 'Non' },
  { value: 'unset', label: 'Non renseigné' },
] as const

/** #113 — « Privée ✅ / Publique ✅ » du retour terrain, et le silence qui reste permis (M21). */
const KIND_OPTIONS = [
  { value: 'private', label: 'Privée' },
  { value: 'public', label: 'Publique' },
  { value: 'unset', label: 'Non renseigné' },
] as const

type BooleanChoice = (typeof PRESENT_OPTIONS)[number]['value']

/**
 * #258 — les choix de la carte « Desserte de réseau EU », **dans les mots du domaine** : la
 * tablette écrit le code, le rapport et le bureau impriment le même libellé (`surveyChoiceLabel`).
 */
const choicesOf = <T extends string>(labels: Readonly<Record<T, string>>) =>
  [
    ...(Object.entries(labels) as [T, string][]).map(([value, label]) => ({ value, label })),
    { value: 'unset' as const, label: 'Non renseigné' },
  ] as const

const DRINKING_WATER_OPTIONS = choicesOf(DRINKING_WATER_SUPPLY_LABELS)
const PUBLIC_NETWORK_OPTIONS = choicesOf(PUBLIC_NETWORK_TYPE_LABELS)
const WASTEWATER_PRODUCTION_OPTIONS = choicesOf(WASTEWATER_PRODUCTION_LABELS)

/** Total / partiel d'un relevage, et le silence qui reste permis (M21). */
const EXTENT_OPTIONS = [
  { value: 'full', label: 'Total' },
  { value: 'partial', label: 'Partiel' },
  { value: 'unset', label: 'Non renseigné' },
] as const

/** Les deux clapets de la fiche : « Existence Clapet EU — Nécessaire ». */
const CHECK_VALVES = [
  { key: 'wastewaterCheckValve', label: 'Clapet EU' },
  { key: 'stormwaterCheckValve', label: 'Clapet EP' },
] as const

/** Les deux relevages de la fiche : présence, puis total ou partiel. */
const PUMPS = [
  { key: 'wastewaterPump', label: 'Relevage EU' },
  { key: 'stormwaterPump', label: 'Relevage EP' },
] as const

/** Les deux boîtes de raccordement, mêmes champs et mêmes mots que la fiche papier. */
const CONNECTION_BOXES = [
  { key: 'wastewaterConnectionBox', label: 'Boîte de raccordement EU' },
  { key: 'stormwaterConnectionBox', label: 'Boîte de raccordement EP' },
] as const

type ConnectionBoxKey = (typeof CONNECTION_BOXES)[number]['key']

type EquipmentPatch = Partial<NonNullable<SurveyV2['connectionEquipment']>>

type PlotPatch = Partial<
  Pick<
    NonNullable<SurveyV2['plot']>,
    'areaM2' | 'drinkingWaterSupply' | 'publicNetworkType' | 'wastewaterProduction' | 'well'
  >
>

export function SurveyForm({
  survey,
  hiddenBlocks = [],
  vocabulary = [],
  disabled,
  onChange,
  anomalies = [],
  onFlagFixture,
  beforeFixtures,
  afterFixtures,
}: SurveyFormProps) {
  const methods = survey.methodsUsed ?? []

  function toggleMethod(method: string, checked: boolean): void {
    const next = checked ? [...methods, method] : methods.filter((kept) => kept !== method)
    onChange?.({ ...survey, methodsUsed: next })
  }

  function setEquipment(patch: EquipmentPatch): void {
    onChange?.({ ...survey, connectionEquipment: { ...survey.connectionEquipment, ...patch } })
  }

  const equipment = survey.connectionEquipment ?? {}
  const plot = survey.plot ?? {}
  // « Desserte de réseau EU » sert deux blocs : le terrain lui-même, et le réseau public, qui nourrit
  // aussi les anomalies (`TABLET_BLOCKS`). La carte reste tant que l'un des deux est demandé.
  const askPlot = !hiddenBlocks.includes('plot')
  const askNetwork = !hiddenBlocks.includes('public_network')

  function setPlot(patch: PlotPatch): void {
    onChange?.({ ...survey, plot: { ...survey.plot, ...patch } })
  }

  function setWell(patch: NonNullable<NonNullable<SurveyV2['plot']>['well']>): void {
    setPlot({ well: { ...plot.well, ...patch } })
  }

  function setPresence(
    key: 'septicTank' | 'greaseTrap',
    choice: PresenceChoice | PresenceAssessment,
  ): void {
    const value = choice === 'unset' ? null : choice

    setEquipment(key === 'septicTank' ? { septicTank: value } : { greaseTrap: value })
  }

  /**
   * ⚠️ Le patch est un `Partial<ConnectionBox>` **du domaine**, pas une redéclaration de la forme :
   * une nature de plus ou un champ de plus doit faire réagir le compilateur ici, pas passer
   * inaperçu.
   */
  function setConnectionBox(key: ConnectionBoxKey, patch: Partial<ConnectionBox>): void {
    const next = { ...survey.connectionEquipment?.[key], ...patch }

    setEquipment(
      key === 'wastewaterConnectionBox'
        ? { wastewaterConnectionBox: next }
        : { stormwaterConnectionBox: next },
    )
  }

  return (
    <div className={s.sections}>
      {!hiddenBlocks.includes('methods') && (
        <Card className={s.card}>
          {/* ⚠️ Codes de la fiche papier, affichés verbatim : ce sont les mots imprimés que
            l'agent coche depuis des années, pas des identifiants à traduire.

            ⚠️ Quatre cases, et jamais une cinquième : un `COMCOM` que porterait encore un
            brouillon d'avant la v2 ne s'affiche pas, parce qu'il n'y a rien à y lire — la case
            ne nomme aucune collectivité (#117). `upgradeSurvey` la retire à la transmission. */}
          <h2 className={s.title}>Méthodes utilisées</h2>
          <div className={s.checkboxes}>
            {SURVEY_METHODS.map((method) => (
              <Checkbox
                key={method}
                label={method}
                checked={methods.includes(method)}
                disabled={disabled}
                onCheckedChange={(checked) => toggleMethod(method, checked)}
              />
            ))}
          </div>
        </Card>
      )}

      {(askPlot || askNetwork) && (
        <Card className={s.card}>
          <h2 className={s.title}>Desserte de réseau EU</h2>
          <div className={s.presences}>
            {askPlot && (
              <>
                <div className={s.fields}>
                  <DecimalField
                    label="Superficie du terrain (m²)"
                    defaultValue={plot.areaM2 ?? null}
                    disabled={disabled}
                    onValueChange={(areaM2) => setPlot({ areaM2 })}
                  />
                </div>
                <ChoiceField
                  label="Raccordement au réseau AEP"
                  options={DRINKING_WATER_OPTIONS}
                  value={choiceOf(plot.drinkingWaterSupply, DRINKING_WATER_SUPPLY_LABELS)}
                  disabled={disabled}
                  onValueChange={(choice) => setPlot({ drinkingWaterSupply: unset(choice) })}
                />
              </>
            )}
            {askNetwork && (
              <ChoiceField
                label="Réseau d’assainissement public"
                options={PUBLIC_NETWORK_OPTIONS}
                value={choiceOf(plot.publicNetworkType, PUBLIC_NETWORK_TYPE_LABELS)}
                disabled={disabled}
                onValueChange={(choice) => setPlot({ publicNetworkType: unset(choice) })}
              />
            )}
            {askPlot && (
              <>
                <ChoiceField
                  label="Production d’eaux usées"
                  options={WASTEWATER_PRODUCTION_OPTIONS}
                  value={choiceOf(plot.wastewaterProduction, WASTEWATER_PRODUCTION_LABELS)}
                  disabled={disabled}
                  onValueChange={(choice) => setPlot({ wastewaterProduction: unset(choice) })}
                />
                <fieldset className={s.group}>
                  <legend className={s.groupTitle}>Puits sur la parcelle</legend>
                  <div className={s.pair}>
                    <ChoiceField
                      label="Puits — présence"
                      shortLabel="Présence"
                      options={PRESENT_OPTIONS}
                      value={presentChoice(plot.well?.present)}
                      disabled={disabled}
                      onValueChange={(choice) => setWell({ present: presentValue(choice) })}
                    />
                    <ChoiceField
                      label="Puits — déclaré"
                      shortLabel="Déclaré"
                      options={PRESENT_OPTIONS}
                      value={presentChoice(plot.well?.declared)}
                      disabled={disabled}
                      onValueChange={(choice) => setWell({ declared: presentValue(choice) })}
                    />
                  </div>
                  <TextField
                    label="Usages du puits"
                    hint="Séparés par des virgules — « arrosage, WC »."
                    defaultValue={plot.well?.uses?.join(', ') ?? null}
                    disabled={disabled}
                    onValueChange={(raw) => setWell({ uses: usesOf(raw) })}
                  />
                </fieldset>
              </>
            )}
          </div>
        </Card>
      )}

      {beforeFixtures}

      <IndoorFixtures
        survey={survey}
        vocabulary={vocabulary}
        disabled={disabled}
        onChange={onChange}
        anomalies={anomalies}
        onFlag={onFlagFixture}
      />

      {afterFixtures}

      {!hiddenBlocks.includes('connection_equipment') && (
        <Card className={s.card}>
          <h2 className={s.title}>Équipements de raccordement</h2>
          <div className={s.presences}>
            <ChoiceField
              label="Fosse"
              options={PRESENCE_OPTIONS}
              value={survey.connectionEquipment?.septicTank ?? 'unset'}
              disabled={disabled}
              onValueChange={(choice) => setPresence('septicTank', choice)}
            />
            <ChoiceField
              label="Dégraisseur"
              options={PRESENCE_OPTIONS}
              value={survey.connectionEquipment?.greaseTrap ?? 'unset'}
              disabled={disabled}
              onValueChange={(choice) => setPresence('greaseTrap', choice)}
            />

            {CONNECTION_BOXES.map((box) => {
              const value = survey.connectionEquipment?.[box.key] ?? {}

              return (
                <fieldset key={box.key} className={s.group}>
                  <legend className={s.groupTitle}>{box.label}</legend>
                  <ChoiceField
                    label={`${box.label} — présence`}
                    shortLabel="Présence"
                    options={PRESENT_OPTIONS}
                    value={presentChoice(value.present)}
                    disabled={disabled}
                    onValueChange={(choice) =>
                      setConnectionBox(box.key, { present: presentValue(choice) })
                    }
                  />
                  {/* #113 — « Privée / Publique » : la forme existait sur les servitudes, elle
                    manquait à la boîte. Facultative : on enregistre et on signale (M21). */}
                  <ChoiceField
                    label={`${box.label} — nature`}
                    shortLabel="Nature"
                    options={KIND_OPTIONS}
                    value={value.kind ?? 'unset'}
                    disabled={disabled}
                    onValueChange={(choice) =>
                      setConnectionBox(box.key, { kind: choice === 'unset' ? null : choice })
                    }
                  />
                  <TextField
                    label={`${box.label} — position`}
                    shortLabel="Position"
                    defaultValue={value.position ?? null}
                    disabled={disabled}
                    onValueChange={(position) => setConnectionBox(box.key, { position })}
                  />
                </fieldset>
              )
            })}

            {/* #258 — les sept équipements que la fiche porte et que l'écran ne saisissait pas :
              ils étaient au schéma et au rapport, qui les imprimait « Non renseigné ». */}
            <ChoiceField
              label="Sous-sol / cave"
              options={PRESENT_OPTIONS}
              value={presentChoice(equipment.basement?.present)}
              disabled={disabled}
              onValueChange={(choice) =>
                setEquipment({ basement: { ...equipment.basement, present: presentValue(choice) } })
              }
            />
            {CHECK_VALVES.map((valve) => (
              <fieldset key={valve.key} className={s.group}>
                <legend className={s.groupTitle}>{valve.label}</legend>
                <div className={s.pair}>
                  <ChoiceField
                    label={`${valve.label} — existant`}
                    shortLabel="Existant"
                    options={PRESENT_OPTIONS}
                    value={presentChoice(equipment[valve.key]?.exists)}
                    disabled={disabled}
                    onValueChange={(choice) =>
                      setEquipment({
                        [valve.key]: { ...equipment[valve.key], exists: presentValue(choice) },
                      })
                    }
                  />
                  <ChoiceField
                    label={`${valve.label} — nécessaire`}
                    shortLabel="Nécessaire"
                    options={PRESENT_OPTIONS}
                    value={presentChoice(equipment[valve.key]?.required)}
                    disabled={disabled}
                    onValueChange={(choice) =>
                      setEquipment({
                        [valve.key]: { ...equipment[valve.key], required: presentValue(choice) },
                      })
                    }
                  />
                </div>
              </fieldset>
            ))}
            {PUMPS.map((pump) => (
              <fieldset key={pump.key} className={s.group}>
                <legend className={s.groupTitle}>{pump.label}</legend>
                <div className={s.pair}>
                  <ChoiceField
                    label={`${pump.label} — présence`}
                    shortLabel="Présence"
                    options={PRESENT_OPTIONS}
                    value={presentChoice(equipment[pump.key]?.present)}
                    disabled={disabled}
                    onValueChange={(choice) =>
                      setEquipment({
                        [pump.key]: { ...equipment[pump.key], present: presentValue(choice) },
                      })
                    }
                  />
                  <ChoiceField
                    label={`${pump.label} — étendue`}
                    shortLabel="Étendue"
                    options={EXTENT_OPTIONS}
                    value={equipment[pump.key]?.extent ?? 'unset'}
                    disabled={disabled}
                    onValueChange={(choice) =>
                      setEquipment({
                        [pump.key]: { ...equipment[pump.key], extent: unset(choice) },
                      })
                    }
                  />
                </div>
              </fieldset>
            ))}
            <fieldset className={s.group}>
              <legend className={s.groupTitle}>Servitudes EU</legend>
              <div className={s.pair}>
                <ChoiceField
                  label="Servitudes EU — présence"
                  shortLabel="Présence"
                  options={PRESENT_OPTIONS}
                  value={presentChoice(equipment.wastewaterEasements?.present)}
                  disabled={disabled}
                  onValueChange={(choice) =>
                    setEquipment({
                      wastewaterEasements: {
                        ...equipment.wastewaterEasements,
                        present: presentValue(choice),
                      },
                    })
                  }
                />
                <ChoiceField
                  label="Servitudes EU — nature"
                  shortLabel="Nature"
                  options={KIND_OPTIONS}
                  value={equipment.wastewaterEasements?.kind ?? 'unset'}
                  disabled={disabled}
                  onValueChange={(choice) =>
                    setEquipment({
                      wastewaterEasements: {
                        ...equipment.wastewaterEasements,
                        kind: unset(choice),
                      },
                    })
                  }
                />
              </div>
            </fieldset>
            <fieldset className={s.group}>
              <legend className={s.groupTitle}>Siphon EU</legend>
              <div className={s.pair}>
                <ChoiceField
                  label="Siphon EU — présence"
                  shortLabel="Présence"
                  options={PRESENT_OPTIONS}
                  value={presentChoice(equipment.wastewaterTrap?.present)}
                  disabled={disabled}
                  onValueChange={(choice) =>
                    setEquipment({
                      wastewaterTrap: {
                        ...equipment.wastewaterTrap,
                        present: presentValue(choice),
                      },
                    })
                  }
                />
                <TextField
                  label="Siphon EU — position"
                  shortLabel="Position"
                  defaultValue={equipment.wastewaterTrap?.position ?? null}
                  disabled={disabled}
                  onValueChange={(position) =>
                    setEquipment({ wastewaterTrap: { ...equipment.wastewaterTrap, position } })
                  }
                />
              </div>
            </fieldset>
          </div>
        </Card>
      )}
    </div>
  )
}

/** Le booléen de la fiche, tel que le segment le montre — l'absence de réponse comprise. */
function presentChoice(present: boolean | null | undefined): BooleanChoice {
  if (present === true) return 'yes'
  if (present === false) return 'no'

  return 'unset'
}

function presentValue(choice: BooleanChoice): boolean | null {
  if (choice === 'yes') return true
  if (choice === 'no') return false

  return null
}

/** La sentinelle « Non renseigné » des segments, retraduite en `null` pour le relevé (M21). */
function unset<T extends string>(choice: T | 'unset'): T | null {
  return choice === 'unset' ? null : choice
}

/**
 * Le segment qu'une valeur du relevé allume. ⚠️ Un texte libre d'une fiche d'avant la liste
 * n'allume rien et **n'est pas effacé** : il reste au relevé tant que l'agent ne choisit pas (M21).
 */
function choiceOf<T extends string>(
  value: string | null | undefined,
  labels: Readonly<Record<T, string>>,
): T | 'unset' {
  return value != null && Object.hasOwn(labels, value) ? (value as T) : 'unset'
}

/** Les usages du puits, un par entrée : « arrosage, WC » devient `['arrosage', 'WC']`. */
function usesOf(raw: string | null): string[] {
  return (raw ?? '')
    .split(',')
    .map((use) => use.trim())
    .filter((use) => use !== '')
}

/**
 * Case à cocher — `<input type="checkbox">` natif : rôle, focus et clavier viennent du
 * navigateur, et sa hauteur de 46 px vient de la densité (`--control-h`), pas d'une variante.
 *
 * ⚠️ À promouvoir dans `@ghp/ui` : c'est une *chose* stylée, sa place est au design system
 * dès qu'un second écran en aura besoin (`packages/ui/CLAUDE.md`).
 */
function Checkbox({
  label,
  checked,
  disabled,
  onCheckedChange,
}: {
  label: string
  checked: boolean
  disabled?: boolean
  onCheckedChange: (checked: boolean) => void
}) {
  return (
    <label className={s.checkbox}>
      <input
        type="checkbox"
        checked={checked}
        disabled={disabled}
        onChange={(event) => onCheckedChange(event.target.checked)}
      />
      {label}
    </label>
  )
}

/**
 * Ce que la frappe devient dans le relevé. `invalid` quand une saisie non vide n'est **pas**
 * retenue : la garder en silence transmettrait autre chose que ce que l'écran affiche —
 * « 1 970 » (espace du pavé numérique Android) tronqué en `1` est exactement le cas que M21
 * n'autorise pas. Les séparateurs d'espace, eux, sont tolérés et retirés.
 */
export function parseCount(raw: string): { value: number | null; invalid: boolean } {
  const cleaned = raw.replace(/\s/gu, '')
  if (cleaned === '') return { value: null, invalid: false }

  const parsed = Number(cleaned)
  if (Number.isInteger(parsed) && parsed >= 0) return { value: parsed, invalid: false }

  return { value: null, invalid: true }
}

export function NumberField({
  label,
  defaultValue,
  disabled,
  onValueChange,
}: {
  label: string
  defaultValue: number | null
  disabled?: boolean
  onValueChange: (value: number | null) => void
}) {
  const [invalid, setInvalid] = useState(false)

  return (
    <Field
      label={label}
      // Une saisie non retenue **se dit** (M21) : le champ affiche la frappe, le brouillon rien.
      warning={invalid ? 'Valeur non numérique — non enregistrée.' : null}
    >
      {({ id, hintId }) => (
        <Input
          id={id}
          aria-describedby={hintId}
          // Le pavé numérique de la tablette, sans les flèches d'un `type="number"` — deux
          // cibles de 12 px collées au champ, injouables au doigt.
          inputMode="numeric"
          defaultValue={defaultValue ?? ''}
          disabled={disabled}
          invalid={invalid}
          onValueChange={(raw) => {
            const parsed = parseCount(raw)
            setInvalid(parsed.invalid)
            onValueChange(parsed.value)
          }}
        />
      )}
    </Field>
  )
}

/** Une mesure décimale du relevé, sur le modèle de {@link NumberField}. */
function DecimalField({
  label,
  defaultValue,
  disabled,
  onValueChange,
}: {
  label: string
  defaultValue: number | null
  disabled?: boolean
  onValueChange: (value: number | null) => void
}) {
  const [invalid, setInvalid] = useState(false)

  return (
    <Field label={label} warning={invalid ? 'Valeur non numérique — non enregistrée.' : null}>
      {({ id, hintId }) => (
        <Input
          id={id}
          aria-describedby={hintId}
          inputMode="decimal"
          defaultValue={defaultValue ?? ''}
          disabled={disabled}
          invalid={invalid}
          onValueChange={(raw) => {
            const parsed = parseMeasure(raw)
            setInvalid(parsed.invalid)
            onValueChange(parsed.value)
          }}
        />
      )}
    </Field>
  )
}

/**
 * Un champ libre du relevé. Non contrôlé pour la même raison que `NumberField` : la frappe vit
 * dans le DOM, le brouillon suit à chaque touche.
 *
 * `shortLabel` est ce que l'agent lit ; `label` reste le nom complet du champ, celui qui
 * distingue « position » de la boîte EU de celle de la boîte EP pour un lecteur d'écran.
 */
export function TextField({
  label,
  shortLabel,
  hint,
  defaultValue,
  disabled,
  onValueChange,
}: {
  label: string
  shortLabel?: string
  hint?: string
  defaultValue: string | null
  disabled?: boolean
  onValueChange: (value: string | null) => void
}) {
  return (
    <Field label={shortLabel ?? label} hint={hint}>
      {({ id, hintId }) => (
        <Input
          id={id}
          // ⚠️ Le nom complet se pose sur le **champ**, jamais sur le `<label>` : sans rôle ARIA,
          // il n'accepte pas `aria-label`, et les outils lisent son `textContent`.
          aria-label={shortLabel === undefined ? undefined : label}
          aria-describedby={hintId}
          defaultValue={defaultValue ?? ''}
          disabled={disabled}
          onValueChange={(raw) => onValueChange(raw.trim() === '' ? null : raw)}
        />
      )}
    </Field>
  )
}

/**
 * Un choix parmi des segments, l'absence de réponse comprise. Sert aux tri-états de M16 comme
 * aux Oui/Non de la fiche : ce sont les mêmes segments, et deux composants divergeraient.
 */
function ChoiceField<T extends string>({
  label,
  shortLabel,
  options,
  value,
  disabled,
  onValueChange,
}: {
  label: string
  shortLabel?: string
  options: readonly { value: T; label: string }[]
  value: T
  disabled?: boolean
  onValueChange: (choice: T) => void
}) {
  return (
    // Le groupe se nomme par `label` (nom complet), le libellé visible n'en est que l'abrégé.
    <Field label={shortLabel ?? label} className={disabled === true ? s.disabled : undefined}>
      {() => (
        // ⚠️ `disabled` est **transmis**, pas seulement peint : sinon les segments d'une fiche
        // transmise restent focalisables, cliquables, et annoncés actifs.
        <SegmentedControl
          label={label}
          options={options}
          value={value}
          disabled={disabled}
          onValueChange={onValueChange}
        />
      )}
    </Field>
  )
}
